import api from 'src/utils/ajax';
import { decamelize } from 'src/utils/decamelize';

import { denormalize } from '../shared/normalizers';

import { normalizeTrainer } from './normalizers';

/** @typedef { import('./_types').Trainer } Trainer */

/** @type {() => Promise<Trainer>} */
export const fetchCurrentTrainer = async () => {
  const response = await api.get(`/api/v4/trainers/current`);
  return normalizeTrainer(response.data);
};
// /api/v3/trainers?fetch_all=true&q%5Bs%5D=trainer_first_name%20asc
/** @type {(params?: Record<string, any>) => Promise<Trainer[]>} */
export const fetchAllTrainers = async (params = {}) => {
  const response = await api.get(`/api/v3/trainers`, {
    params: {
      fetch_all: true,
      ...params,
    },
  });
  return response.data.map((t) => normalizeTrainer(t));
};

/** @type {(options: { page?: number, per?: number, search?: string, sortBy?: string, sortDir?: string }) => Promise<{ trainers: Trainer[], total: number }>} */
export const fetchTrainers = async ({
  page = 1,
  per = 10,
  search = '',
  sortBy = '',
  sortDir = '',
}) => {
  const response = await api.get(`/api/v3/trainers`, {
    params: {
      page,
      per,
      'q[trainer_nickname_or_trainer_first_name_or_trainer_last_name_or_trainer_email_cont]':
        search,
      'q[s]': sortBy ? `${decamelize(sortBy || '')} ${sortDir || ''}` : '',
    },
  });
  return {
    trainers: response.data.trainer.map((t) => normalizeTrainer(t)),
    total: response.data?.meta?.total,
  };
};

/** @type {(options: { trainerId: Trainer['id'], deleteAccount?: boolean }) => Promise<Trainer>} */
export const deleteTrainer = async ({ trainerId, deleteAccount = false }) => {
  const response = await api.delete(`/api/v3/trainers/${trainerId}`, {
    data: { delete_account: deleteAccount },
  });
  return normalizeTrainer(response.data);
};

/**
 * @typedef { object } FetchUserOptions
 * @prop { Trainer['id'] } id
 */
/** @type {(options: FetchUserOptions) => Promise<Trainer>} */
export const fetchTrainer = async ({ id }) => {
  const response = await api.get(`/api/v4/users/${id}`);
  return normalizeTrainer(response.data);
};

/** @typedef { import('./_types').TransferResponse } TransferResponse */

/** @type {(trainerId: Trainer['id'], amount: number, memo: string) => Promise<TransferResponse>} */
export const transferToTrainer = async (trainerId, amount, memo) => {
  const response = await api.post(`/api/v4/trainers/${trainerId}/transfer_to`, {
    amount,
    memo,
  });

  return response.data;
};

/** @type { (trainer: Trainer) => Promise<Trainer> } */
export const createTrainer = async (trainer) => {
  const response = await api.post(`/api/v3/trainers`, {
    trainer: denormalize(trainer),
  });
  return normalizeTrainer(response.data);
};

/** @type {(options: { trainerId: Trainer['id'] }) => Promise<Trainer>} */
export const fetchTrainerByTrainerId = async ({ trainerId }) => {
  const response = await api.get(`/api/v3/trainers/${trainerId}`);
  return normalizeTrainer(response.data);
};

/** @type { (trainerId: Trainer['id'], trainer: Partial<Trainer>) => Promise<Trainer> } */
export const updateTrainer = async (trainerId, trainer) => {
  const response = await api.put(`/api/v3/trainers/${trainerId}`, {
    trainer: {
      ...denormalize(trainer),
      tag_list: trainer.tags?.join(',') || '',
    },
  });
  return normalizeTrainer(response.data);
};
